import React, { useEffect, useRef, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretLeft,
  CaretRight,
  Clock,
  Pause,
  Play,
} from "@phosphor-icons/react";
import type { ResearchModelBundle } from "../research";
import type { ComputeRentalPanel, ComputeRentalSnapshot, CpuRentalTier, RentalGpuSegment, RunpodRentalQuote } from "../types";

function compact(value: number) {
  return value.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 });
}

function delta(value: number | null) {
  if (value == null) return "暂无同比期";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}% MoM`;
}

function signed(value: number, suffix: string) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}${suffix}`;
}

interface TrendOverlay {
  label: string;
  className: string;
  values: Array<number | null>;
}

function ChartExpandButton({ expanded, onToggle, label }: { expanded: boolean; onToggle: () => void; label: string }) {
  const action = expanded ? "退出放大" : "放大图表";
  return <button
    type="button"
    className="chart-expand"
    onClick={onToggle}
    aria-label={`${action}：${label}`}
    aria-pressed={expanded}
    title={action}
    data-chart-expand="true"
  >
    {expanded ? <ArrowsInSimple size={17} aria-hidden="true" /> : <ArrowsOutSimple size={17} aria-hidden="true" />}
  </button>;
}

function MonitorTrendChart({
  points,
  label,
  unit,
  format,
  seriesLabel,
  overlays,
}: {
  points: Array<{ period: string; value: number }>;
  label: string;
  unit: string;
  format: (value: number) => string;
  seriesLabel?: string;
  overlays?: TrendOverlay[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  if (points.length < 2) return null;
  const width = 720;
  const height = 310;
  const padding = { top: 26, right: 16, bottom: 46, left: 68 };
  const values = [
    ...points.map((point) => point.value),
    ...(overlays ?? []).flatMap((overlay) => overlay.values.flatMap((value) => value == null ? [] : [value])),
  ];
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const spread = Math.max(rawMax - rawMin, Math.abs(rawMax) * 0.08, 1);
  const min = Math.max(0, rawMin - spread * 0.1);
  const max = rawMax + spread * 0.12;
  const x = (index: number) => padding.left + index / Math.max(points.length - 1, 1) * (width - padding.left - padding.right);
  const y = (value: number) => padding.top + (max - value) / Math.max(max - min, 1e-9) * (height - padding.top - padding.bottom);
  const linePoints = points.map((point, index) => {
    return `${x(index).toFixed(1)},${y(point.value).toFixed(1)}`;
  }).join(" ");
  const xTickIndexes = [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])];
  const activePoint = activeIndex == null ? null : points[activeIndex];
  const activeXPercent = activeIndex == null ? 0 : x(activeIndex) / width * 100;
  const readoutIndex = activeIndex ?? points.length - 1;
  const readoutPoint = points[readoutIndex];

  return (
    <figure
      className={`monitor-trend${expanded ? " chart-is-expanded" : ""}`}
      data-chart-interactive="true"
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? "true" : undefined}
      aria-label={expanded ? `${label}放大图表` : undefined}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") setActiveIndex(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") setExpanded(false);
      }}
    >
      <ChartExpandButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} label={label} />
      <strong className="chart-expanded-title">{label}</strong>
      <svg className="monitor-spark" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
        <title>{label}</title>
        <text x={padding.left} y={padding.top - 8} className="monitor-spark__axis-title">Y · {unit}</text>
        {[0, 0.5, 1].map((ratio) => {
          const tickValue = min + (max - min) * ratio;
          const tickY = y(tickValue);
          return (
            <g key={ratio}>
              <line x1={padding.left} x2={width - padding.right} y1={tickY} y2={tickY} className="monitor-spark__grid" />
              <text x={padding.left - 6} y={tickY + 3} textAnchor="end" className="monitor-spark__axis-label">{format(tickValue)}</text>
            </g>
          );
        })}
        <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="monitor-spark__axis" />
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="monitor-spark__axis" />
        {(overlays ?? []).map((overlay) => {
          const overlayPoints = overlay.values.flatMap((value, index) => value == null ? [] : [`${x(index).toFixed(1)},${y(value).toFixed(1)}`]);
          return overlayPoints.length >= 2
            ? <polyline key={overlay.label} className={`monitor-spark__overlay ${overlay.className}`} points={overlayPoints.join(" ")} fill="none" vectorEffect="non-scaling-stroke" />
            : null;
        })}
        <polyline points={linePoints} fill="none" vectorEffect="non-scaling-stroke" />
        {xTickIndexes.map((index) => (
          <g key={points[index].period}>
            <line x1={x(index)} x2={x(index)} y1={height - padding.bottom} y2={height - padding.bottom + 4} className="monitor-spark__axis" />
            <text
              x={x(index)}
              y={height - 7}
              textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
              className="monitor-spark__axis-label"
            >
              {points[index].period}
            </text>
          </g>
        ))}
        {points.map((point, index) => {
          const previousX = index > 0 ? x(index - 1) : padding.left;
          const nextX = index < points.length - 1 ? x(index + 1) : width - padding.right;
          const left = index === 0 ? padding.left : (previousX + x(index)) / 2;
          const right = index === points.length - 1 ? width - padding.right : (x(index) + nextX) / 2;
          return (
            <rect
              key={`${point.period}-zone`}
              x={left}
              y={padding.top}
              width={Math.max(right - left, 1)}
              height={height - padding.top - padding.bottom}
              className="monitor-spark__interaction-zone"
              role="button"
              tabIndex={0}
              aria-label={`${point.period}：${format(point.value)}`}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") setActiveIndex(index);
              }}
              onPointerDown={(event) => {
                setActiveIndex((current) => event.pointerType !== "mouse" && current === index ? null : index);
              }}
              onFocus={() => setActiveIndex(index)}
              onBlur={() => setActiveIndex(null)}
            >
              <title>{`${point.period}：${format(point.value)}`}</title>
            </rect>
          );
        })}
        {activeIndex != null && activePoint ? (
          <g className="monitor-spark__active">
            <line x1={x(activeIndex)} x2={x(activeIndex)} y1={padding.top} y2={height - padding.bottom} />
            {(overlays ?? []).map((overlay) => overlay.values[activeIndex] == null
              ? null
              : <circle key={overlay.label} className={`monitor-spark__overlay ${overlay.className}`} cx={x(activeIndex)} cy={y(overlay.values[activeIndex]!)} r="3.5" />)}
            <circle cx={x(activeIndex)} cy={y(activePoint.value)} r="4.5" />
          </g>
        ) : null}
      </svg>
      {overlays?.length ? (
        <div className="monitor-trend__legend" aria-hidden="true">
          <span className="monitor-trend__legend-primary">{seriesLabel ?? unit}</span>
          {overlays.map((overlay) => <span key={overlay.label} className={overlay.className}>{overlay.label}</span>)}
        </div>
      ) : null}
      {activePoint ? (
        <div
          className={`monitor-trend__tooltip${activeXPercent > 68 ? " monitor-trend__tooltip--left" : activeXPercent < 32 ? " monitor-trend__tooltip--right" : ""}`}
          style={{ left: `${activeXPercent}%` }}
          role="status"
          aria-live="polite"
        >
          <strong>{activePoint.period}</strong>
          <span>{format(activePoint.value)}</span>
          <small>{unit}</small>
          {activeIndex == null ? null : (overlays ?? []).map((overlay) => overlay.values[activeIndex] == null
            ? null
            : <em key={overlay.label} className={`monitor-trend__tooltip-overlay ${overlay.className}`}>{overlay.label} {format(overlay.values[activeIndex]!)}</em>)}
        </div>
      ) : null}
      <div className="chart-touch-readout" role="status" aria-live="polite" data-chart-touch-readout="true">
        <header>
          <span>当前注释</span>
          <strong>{readoutPoint.period}</strong>
        </header>
        <div className="chart-touch-readout__rows">
          <div>
            <em>{seriesLabel ?? unit}</em>
            <b>{format(readoutPoint.value)}</b>
          </div>
          {(overlays ?? []).map((overlay) => overlay.values[readoutIndex] == null ? null : (
            <div key={`${overlay.label}-touch`}>
              <em className={overlay.className}>{overlay.label}</em>
              <b>{format(overlay.values[readoutIndex]!)}</b>
            </div>
          ))}
        </div>
      </div>
      <figcaption>
        <span className="interaction-hint--pointer">悬停或点击查看逐月明细</span>
        <span className="interaction-hint--touch">点击图表切换月份；明细显示在图表下方</span>
      </figcaption>
    </figure>
  );
}

function Change({ value }: { value: number | null }) {
  const positive = value != null && value >= 0;
  return (
    <span className={`monitor-change ${positive ? "monitor-change--up" : "monitor-change--down"}`}>
      {value == null ? <Clock size={15} /> : positive ? <ArrowUpRight size={15} /> : <ArrowDownRight size={15} />}
      {delta(value)}
    </span>
  );
}

type RentalSeriesKey = "vast" | "community" | "secure";

const RENTAL_SERIES: Array<{ key: RentalSeriesKey; label: string; className: string }> = [
  { key: "vast", label: "Vast 中位", className: "rental-line--vast" },
  { key: "community", label: "RunPod Community", className: "rental-line--community" },
  { key: "secure", label: "RunPod Secure", className: "rental-line--secure" },
];

interface RentalChartDatum {
  median: number;
  min: number;
  max: number | null;
  offers: number;
  source: "Getflops 历史" | "本站 R2";
}

interface RentalChartPoint {
  date: string;
  vast: number | null;
  community: number | null;
  secure: number | null;
  detail: Partial<Record<RentalSeriesKey, RentalChartDatum>>;
}

function rentalHistoryPoints(panel: ComputeRentalPanel, sku: string): RentalChartPoint[] {
  const byDate = new Map<string, RentalChartPoint>();
  const ensure = (date: string) => {
    const existing = byDate.get(date);
    if (existing) return existing;
    const point: RentalChartPoint = { date, vast: null, community: null, secure: null, detail: {} };
    byDate.set(date, point);
    return point;
  };
  const providerKey = {
    vast: "vast",
    "runpod-community": "community",
    "runpod-secure": "secure",
  } as const;
  for (const series of panel.gpuPriceHistory?.series.filter((item) => item.sku === sku) ?? []) {
    const key = providerKey[series.provider];
    for (const sourcePoint of series.points) {
      const point = ensure(sourcePoint.date);
      point[key] = sourcePoint.medianPricePerGpuHourUsd;
      point.detail[key] = {
        median: sourcePoint.medianPricePerGpuHourUsd,
        min: sourcePoint.minPricePerGpuHourUsd,
        max: sourcePoint.maxPricePerGpuHourUsd,
        offers: sourcePoint.offerCount,
        source: "Getflops 历史",
      };
    }
  }
  for (const snapshot of panel.history) {
    const row = snapshot.rows.find((item) => item.sku === sku);
    if (!row) continue;
    const point = ensure(snapshot.date);
    point.vast = row.vast?.pricePerGpuHourUsd.median ?? null;
    point.community = row.runpodCommunity?.pricePerGpuHourUsd ?? null;
    point.secure = row.runpodSecure?.pricePerGpuHourUsd ?? null;
    if (row.vast) point.detail.vast = {
      median: row.vast.pricePerGpuHourUsd.median,
      min: row.vast.pricePerGpuHourUsd.p25,
      max: row.vast.pricePerGpuHourUsd.p75,
      offers: row.vast.offerCount,
      source: "本站 R2",
    };
    else delete point.detail.vast;
    for (const [key, quote] of [["community", row.runpodCommunity], ["secure", row.runpodSecure]] as const) {
      if (quote) point.detail[key] = {
        median: quote.pricePerGpuHourUsd,
        min: quote.pricePerGpuHourUsd,
        max: quote.pricePerGpuHourUsd,
        offers: 1,
        source: "本站 R2",
      };
      else delete point.detail[key];
    }
  }
  return [...byDate.values()].sort((left, right) => left.date.localeCompare(right.date)).slice(-180);
}

function rollingRentalMedian(points: RentalChartPoint[], index: number, key: RentalSeriesKey) {
  const values = points.slice(Math.max(0, index - 6), index + 1)
    .flatMap((point) => point[key] == null ? [] : [point[key] as number])
    .sort((left, right) => left - right);
  if (!values.length) return null;
  const middle = Math.floor(values.length / 2);
  return values.length % 2 === 0 ? (values[middle - 1] + values[middle]) / 2 : values[middle];
}

function RentalHistoryChart({ panel, sku, label }: { panel: ComputeRentalPanel; sku: string; label: string }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [range, setRange] = useState<30 | 90 | "all">(90);
  const [lineMode, setLineMode] = useState<"trend" | "raw">("trend");
  const [expanded, setExpanded] = useState(false);
  const allPoints = rentalHistoryPoints(panel, sku);
  const latestDate = allPoints.at(-1)?.date;
  const cutoff = latestDate && range !== "all" ? (() => {
    const date = new Date(`${latestDate}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() - range + 1);
    return date.toISOString().slice(0, 10);
  })() : null;
  const points = cutoff ? allPoints.filter((point) => point.date >= cutoff) : allPoints;
  const displayPoints = points.map((point, index) => ({
    ...point,
    vast: lineMode === "trend" ? rollingRentalMedian(points, index, "vast") : point.vast,
    community: lineMode === "trend" ? rollingRentalMedian(points, index, "community") : point.community,
    secure: lineMode === "trend" ? rollingRentalMedian(points, index, "secure") : point.secure,
  }));
  const values = displayPoints.flatMap((point) => RENTAL_SERIES.flatMap((series) => point[series.key] == null ? [] : [point[series.key] as number]));
  if (!values.length) return <p className="monitor-rental__empty">该型号当前没有可比报价。</p>;
  const width = 640;
  const height = 280;
  const padding = { top: 22, right: 13, bottom: 44, left: 60 };
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const spread = Math.max(rawMax - rawMin, rawMax * 0.12, 0.1);
  const min = Math.max(0, rawMin - spread * 0.12);
  const max = rawMax + spread * 0.12;
  const x = (index: number) => points.length === 1
    ? padding.left + (width - padding.left - padding.right) / 2
    : padding.left + index / (points.length - 1) * (width - padding.left - padding.right);
  const y = (value: number) => padding.top + (max - value) / (max - min) * (height - padding.top - padding.bottom);
  const ticks = [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])];
  const active = activeIndex == null ? null : points[activeIndex];
  const activeDisplay = activeIndex == null ? null : displayPoints[activeIndex];
  const readoutIndex = activeIndex ?? points.length - 1;
  const readout = points[readoutIndex];
  const readoutDisplay = displayPoints[readoutIndex];

  return (
    <figure
      className={`rental-history${expanded ? " chart-is-expanded" : ""}`}
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? "true" : undefined}
      aria-label={expanded ? `${label}租赁价格放大图表` : undefined}
      onPointerLeave={(event) => event.pointerType === "mouse" && setActiveIndex(null)}
      onKeyDown={(event) => {
        if (event.key === "Escape") setExpanded(false);
      }}
    >
      <ChartExpandButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} label={`${label}租赁价格`} />
      <strong className="chart-expanded-title">{label} · 租赁价格历史</strong>
      <div className="rental-history__toolbar" aria-label="租赁价格历史图选项">
        <div>{([30, 90, "all"] as const).map((option) => <button
          key={option}
          type="button"
          aria-pressed={range === option}
          onClick={() => { setRange(option); setActiveIndex(null); }}
        >{option === "all" ? "全部" : `${option}D`}</button>)}</div>
        <div><button type="button" aria-pressed={lineMode === "trend"} onClick={() => { setLineMode("trend"); setActiveIndex(null); }}>7D 中位</button><button type="button" aria-pressed={lineMode === "raw"} onClick={() => { setLineMode("raw"); setActiveIndex(null); }}>原始</button></div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} Vast 与 RunPod 每卡小时租赁价历史`}>
        <title>{`${label} 每卡小时按需租赁价历史`}</title>
        <text x={padding.left} y="10" className="monitor-spark__axis-title">Y · USD / GPU-hour · {lineMode === "trend" ? "7D median" : "daily raw"}</text>
        {[0, 0.5, 1].map((ratio) => {
          const value = min + (max - min) * ratio;
          const cy = y(value);
          return <g key={ratio}>
            <line x1={padding.left} x2={width - padding.right} y1={cy} y2={cy} className="monitor-spark__grid" />
            <text x={padding.left - 6} y={cy + 3} textAnchor="end" className="monitor-spark__axis-label">${value.toFixed(2)}</text>
          </g>;
        })}
        <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="monitor-spark__axis" />
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="monitor-spark__axis" />
        {RENTAL_SERIES.map((series) => {
          const available = displayPoints.flatMap((point, index) => point[series.key] == null ? [] : [{ index, value: point[series.key] as number }]);
          const latest = available.at(-1);
          return <g key={series.key} className={series.className}>
            {available.length >= 2 ? <polyline points={available.map((point) => `${x(point.index)},${y(point.value)}`).join(" ")} fill="none" /> : null}
            {latest ? <circle cx={x(latest.index)} cy={y(latest.value)} r="2.8" /> : null}
          </g>;
        })}
        {ticks.map((index) => <text
          key={points[index].date}
          x={x(index)}
          y={height - 7}
          textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
          className="monitor-spark__axis-label"
        >{points[index].date.slice(5)}</text>)}
        {points.map((point, index) => {
          const left = index === 0 ? padding.left : (x(index - 1) + x(index)) / 2;
          const right = index === points.length - 1 ? width - padding.right : (x(index) + x(index + 1)) / 2;
          return <rect
            key={point.date}
            x={left}
            y={padding.top}
            width={Math.max(right - left, 1)}
            height={height - padding.top - padding.bottom}
            className="monitor-spark__interaction-zone"
            role="button"
            tabIndex={0}
            aria-label={`${point.date} 租赁报价`}
            onPointerEnter={(event) => {
              if (event.pointerType === "mouse") setActiveIndex(index);
            }}
            onPointerDown={(event) => {
              setActiveIndex((current) => event.pointerType !== "mouse" && current === index ? null : index);
            }}
            onFocus={() => setActiveIndex(index)}
            onBlur={() => setActiveIndex(null)}
          />;
        })}
        {activeIndex != null && activeDisplay ? <g className="rental-history__active">
          <line x1={x(activeIndex)} x2={x(activeIndex)} y1={padding.top} y2={height - padding.bottom} />
          {RENTAL_SERIES.flatMap((series) => activeDisplay[series.key] == null ? [] : [
            <circle key={series.key} className={series.className} cx={x(activeIndex)} cy={y(activeDisplay[series.key] as number)} r="4" />,
          ])}
        </g> : null}
      </svg>
      <div className="rental-history__legend">
        {RENTAL_SERIES.map((series) => <span key={series.key} className={series.className}>{series.label}{lineMode === "trend" ? " · 7D" : ""}</span>)}
      </div>
      {active && activeDisplay ? <div className="rental-history__tooltip" role="status">
        <b>{active.date}</b>
        {RENTAL_SERIES.map((series) => {
          const detail = active.detail[series.key];
          return <span key={series.key}>
            <i>{series.label}{detail ? <small>{detail.source} · 原始 ${detail.median.toFixed(2)} · {detail.offers} 报价 · ${detail.min.toFixed(2)}–{detail.max == null ? `$${detail.median.toFixed(2)}+` : `$${detail.max.toFixed(2)}`}</small> : null}</i>
            <strong>{activeDisplay[series.key] == null ? "—" : `$${activeDisplay[series.key]!.toFixed(2)}`}</strong>
          </span>;
        })}
      </div> : null}
      <div className="chart-touch-readout" role="status" aria-live="polite" data-chart-touch-readout="true">
        <header>
          <span>当前注释</span>
          <strong>{readout.date}</strong>
        </header>
        <div className="chart-touch-readout__rows">
          {RENTAL_SERIES.map((series) => {
            const detail = readout.detail[series.key];
            return <div key={`${series.key}-touch`}>
              <em>{series.label}</em>
              <b>{readoutDisplay[series.key] == null ? "—" : `$${readoutDisplay[series.key]!.toFixed(2)}`}</b>
              {detail ? <small>原始 ${detail.median.toFixed(2)} · {detail.offers} 报价 · {detail.source}</small> : null}
            </div>;
          })}
        </div>
      </div>
      <figcaption>{points.length < 2
        ? `R2 日度历史从 ${points[0].date} 开始积累`
        : `${points[0].date}–${points.at(-1)!.date} · ${points.length} 个采集日 · ${lineMode === "trend" ? "7 日滚动中位；点击显示原始价" : "原始日价"}`}</figcaption>
    </figure>
  );
}

function cpuRentalPrice(snapshot: ComputeRentalSnapshot, sku: string) {
  return snapshot.cpuRows?.find((row) => row.sku === sku)?.pricePerHourUsd ?? null;
}

function CpuRentalHistoryChart({ panel, sku, label }: { panel: ComputeRentalPanel; sku: string; label: string }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const points = panel.history.slice(-90).flatMap((snapshot) => {
    const value = cpuRentalPrice(snapshot, sku);
    return value == null ? [] : [{ date: snapshot.date, value }];
  });
  if (!points.length) return <p className="monitor-rental__empty">该 CPU 规格尚无独立云主机报价。</p>;
  const width = 640;
  const height = 280;
  const padding = { top: 22, right: 13, bottom: 44, left: 60 };
  const values = points.map((point) => point.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const spread = Math.max(rawMax - rawMin, rawMax * 0.12, 0.01);
  const min = Math.max(0, rawMin - spread * 0.12);
  const max = rawMax + spread * 0.12;
  const x = (index: number) => points.length === 1
    ? padding.left + (width - padding.left - padding.right) / 2
    : padding.left + index / (points.length - 1) * (width - padding.left - padding.right);
  const y = (value: number) => padding.top + (max - value) / (max - min) * (height - padding.top - padding.bottom);
  const ticks = [...new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])];
  const active = activeIndex == null ? null : points[activeIndex];
  const readout = active ?? points.at(-1)!;
  const line = points.map((point, index) => `${x(index)},${y(point.value)}`).join(" ");

  return <figure
    className={`rental-history${expanded ? " chart-is-expanded" : ""}`}
    role={expanded ? "dialog" : undefined}
    aria-modal={expanded ? "true" : undefined}
    aria-label={expanded ? `${label}租赁价格放大图表` : undefined}
    onPointerLeave={(event) => event.pointerType === "mouse" && setActiveIndex(null)}
    onKeyDown={(event) => {
      if (event.key === "Escape") setExpanded(false);
    }}
  >
    <ChartExpandButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} label={`${label}租赁价格`} />
    <strong className="chart-expanded-title">{label} · CPU 租赁价格历史</strong>
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} Azure Linux 按需租赁价历史`}>
      <title>{`${label} Azure East US Linux 按需租赁价历史`}</title>
      <text x={padding.left} y="10" className="monitor-spark__axis-title">Y · USD / VM-hour</text>
      {[0, 0.5, 1].map((ratio) => {
        const value = min + (max - min) * ratio;
        const cy = y(value);
        return <g key={ratio}>
          <line x1={padding.left} x2={width - padding.right} y1={cy} y2={cy} className="monitor-spark__grid" />
          <text x={padding.left - 6} y={cy + 3} textAnchor="end" className="monitor-spark__axis-label">${value.toFixed(3)}</text>
        </g>;
      })}
      <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="monitor-spark__axis" />
      <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="monitor-spark__axis" />
      {points.length >= 2 ? <polyline className="rental-line--cpu" points={line} fill="none" /> : null}
      {points.map((point, index) => <circle className="rental-line--cpu" key={point.date} cx={x(index)} cy={y(point.value)} r={points.length === 1 ? 3.5 : 2.2} />)}
      {ticks.map((index) => <text
        key={points[index].date}
        x={x(index)}
        y={height - 7}
        textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
        className="monitor-spark__axis-label"
      >{points[index].date.slice(5)}</text>)}
      {points.map((point, index) => {
        const left = index === 0 ? padding.left : (x(index - 1) + x(index)) / 2;
        const right = index === points.length - 1 ? width - padding.right : (x(index) + x(index + 1)) / 2;
        return <rect
          key={`${point.date}-zone`}
          x={left}
          y={padding.top}
          width={Math.max(right - left, 1)}
          height={height - padding.top - padding.bottom}
          className="monitor-spark__interaction-zone"
          role="button"
          tabIndex={0}
          aria-label={`${point.date}：$${point.value.toFixed(3)} 每虚拟机小时`}
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") setActiveIndex(index);
          }}
          onPointerDown={(event) => {
            setActiveIndex((current) => event.pointerType !== "mouse" && current === index ? null : index);
          }}
          onFocus={() => setActiveIndex(index)}
          onBlur={() => setActiveIndex(null)}
        />;
      })}
    </svg>
    <div className="rental-history__legend"><span className="rental-line--cpu">Azure Linux 按需</span></div>
    {active ? <div className="rental-history__tooltip" role="status">
      <b>{active.date}</b>
      <span>每 VM 小时 <strong>${active.value.toFixed(3)}</strong></span>
    </div> : null}
    <div className="chart-touch-readout" role="status" aria-live="polite" data-chart-touch-readout="true">
      <header>
        <span>当前注释</span>
        <strong>{readout.date}</strong>
      </header>
      <div className="chart-touch-readout__rows">
        <div><em>每 VM 小时</em><b>${readout.value.toFixed(3)}</b></div>
      </div>
    </div>
    <figcaption>{points.length < 2 ? `R2 日度历史从 ${points[0].date} 开始积累` : `${points.length} 个日度快照；点击图表查看逐日明细`}</figcaption>
  </figure>;
}

function stockLabel(quote: RunpodRentalQuote | null) {
  if (!quote) return <span className="rental-stock rental-stock--none">无报价</span>;
  const labels = { High: "高", Medium: "中", Low: "低", None: "无" } as const;
  return <span className={`rental-stock rental-stock--${quote.stockStatus.toLowerCase()}`}>{labels[quote.stockStatus]}库存</span>;
}

function sevenDayChange(panel: ComputeRentalPanel, sku: string) {
  const points = rentalHistoryPoints(panel, sku);
  const latest = points.at(-1);
  if (!latest) return null;
  const target = new Date(`${latest.date}T00:00:00Z`);
  target.setUTCDate(target.getUTCDate() - 7);
  const targetDate = target.toISOString().slice(0, 10);
  const prior = [...points].reverse().find((point) => point.date <= targetDate && point.vast != null);
  const currentPrice = latest.vast;
  const priorPrice = prior?.vast ?? null;
  return currentPrice != null && priorPrice != null && priorPrice > 0 ? (currentPrice / priorPrice - 1) * 100 : null;
}

function cpuSevenDayChange(panel: ComputeRentalPanel, sku: string) {
  const latest = panel.history.at(-1);
  if (!latest) return null;
  const target = new Date(`${latest.date}T00:00:00Z`);
  target.setUTCDate(target.getUTCDate() - 7);
  const prior = [...panel.history].reverse().find((snapshot) => snapshot.date <= target.toISOString().slice(0, 10));
  const currentPrice = cpuRentalPrice(latest, sku);
  const priorPrice = prior ? cpuRentalPrice(prior, sku) : null;
  return currentPrice != null && priorPrice != null && priorPrice > 0 ? (currentPrice / priorPrice - 1) * 100 : null;
}

function ComputeRentalBoard({ panel }: { panel: ComputeRentalPanel }) {
  const [market, setMarket] = useState<"gpu" | "cpu">("gpu");
  const [segment, setSegment] = useState<RentalGpuSegment>("frontier");
  const [selectedSku, setSelectedSku] = useState("h100-sxm");
  const [cpuTier, setCpuTier] = useState<CpuRentalTier>("general");
  const [selectedCpuSku, setSelectedCpuSku] = useState("Standard_D4as_v5");
  const latest = panel.history.at(-1)!;
  const visible = latest.rows.filter((row) => row.segment === segment);
  const selected = latest.rows.find((row) => row.sku === selectedSku && row.segment === segment) ?? visible[0];
  const cpuRows = latest.cpuRows ?? [];
  const visibleCpu = cpuRows.filter((row) => row.tier === cpuTier);
  const selectedCpu = cpuRows.find((row) => row.sku === selectedCpuSku && row.tier === cpuTier) ?? visibleCpu[0];
  const matchingGeneralCpu = selectedCpu?.tier === "memory"
    ? cpuRows.find((row) => row.tier === "general" && row.vcpus === selectedCpu.vcpus)
    : null;
  const cpuPremium = selectedCpu && matchingGeneralCpu
    ? (selectedCpu.pricePerVcpuHourUsd / matchingGeneralCpu.pricePerVcpuHourUsd - 1) * 100
    : null;
  const historicalDays = panel.gpuPriceHistory
    ? new Set(panel.gpuPriceHistory.series.flatMap((series) => series.points.map((point) => point.date))).size
    : panel.history.length;
  const runpodComparison = selected?.runpodSecure ?? selected?.runpodCommunity ?? null;
  const spread = selected?.vast && runpodComparison
    ? (selected.vast.pricePerGpuHourUsd.median / runpodComparison.pricePerGpuHourUsd - 1) * 100
    : null;
  const segmentOptions: Array<{ key: RentalGpuSegment; label: string }> = [
    { key: "frontier", label: "前沿训练" },
    { key: "inference", label: "推理 / 成熟卡" },
    { key: "consumer", label: "消费卡" },
  ];
  const cpuTierOptions: Array<{ key: CpuRentalTier; label: string }> = [
    { key: "general", label: "通用型 D" },
    { key: "memory", label: "内存型 E" },
  ];

  return (
    <section className="monitor-rental" aria-labelledby="monitor-rental-title">
      <header className="monitor-rental__head">
        <div>
          <span id="monitor-rental-title">算力租赁市场</span>
          <small>GPU 真实采集历史 + CPU 独立云主机基准 · 本站日度 R2 续接</small>
        </div>
        <div className="monitor-rental__controls">
          <div className="monitor-rental__segments monitor-rental__segments--market" aria-label="算力租赁资源">
            <button type="button" className={market === "gpu" ? "is-active" : ""} aria-pressed={market === "gpu"} onClick={() => setMarket("gpu")}>GPU</button>
            <button type="button" className={market === "cpu" ? "is-active" : ""} aria-pressed={market === "cpu"} disabled={!cpuRows.length} onClick={() => setMarket("cpu")}>CPU</button>
          </div>
          <div className="monitor-rental__segments" aria-label={market === "gpu" ? "GPU 租赁类型" : "CPU 云主机类型"}>
            {market === "gpu" ? segmentOptions.map((option) => <button
              key={option.key}
              type="button"
              className={segment === option.key ? "is-active" : ""}
              aria-pressed={segment === option.key}
              onClick={() => {
                setSegment(option.key);
                setSelectedSku(latest.rows.find((row) => row.segment === option.key)?.sku ?? selectedSku);
              }}
            >{option.label}</button>) : cpuTierOptions.map((option) => <button
              key={option.key}
              type="button"
              className={cpuTier === option.key ? "is-active" : ""}
              aria-pressed={cpuTier === option.key}
              onClick={() => {
                setCpuTier(option.key);
                setSelectedCpuSku(cpuRows.find((row) => row.tier === option.key)?.sku ?? selectedCpuSku);
              }}
            >{option.label}</button>)}
          </div>
        </div>
        <div className="monitor-rental__sample">
          <b>{latest.sample.vastOfferCount.toLocaleString("en-US")}</b> Vast 样本
          <span>{latest.sample.runpodGpuTypeCount} RunPod 型号</span>
          <span>{latest.sample.azureCpuSkuCount ?? 0} CPU 规格</span>
          <span>{historicalDays} 个历史日</span>
          <time dateTime={latest.capturedAt}>{latest.date}</time>
        </div>
      </header>
      {market === "gpu" ? <div className="monitor-rental__body">
          <details className="monitor-rental__table-fold">
            <summary>展开 {visible.length} 款 GPU 当前报价明细 · 点选一行切换下方走势图</summary>
            <div className="monitor-rental__table-wrap">
            <table>
              <thead><tr>
                <th>GPU / VRAM</th>
                <th>Vast $/卡时</th>
                <th>RunPod 社区</th>
                <th>RunPod 安全</th>
                <th>Vast 7D</th>
                <th>vCPU / 卡</th>
                <th>RAM / 卡</th>
                <th>DLPerf / $</th>
              </tr></thead>
              <tbody>{visible.map((row) => {
                const change = sevenDayChange(panel, row.sku);
                return <tr key={row.sku} className={selected?.sku === row.sku ? "is-selected" : ""}>
                  <td><button type="button" onClick={() => setSelectedSku(row.sku)}><b>{row.label}</b><small>{row.vramGb}GB</small></button></td>
                  <td title={row.vast ? `P25 $${row.vast.pricePerGpuHourUsd.p25.toFixed(2)}；P75 $${row.vast.pricePerGpuHourUsd.p75.toFixed(2)}；${row.vast.offerCount} 个报价` : "当前无 Vast 可比报价"}>
                    {row.vast ? <><b>${row.vast.pricePerGpuHourUsd.median.toFixed(2)}</b><small>${row.vast.pricePerGpuHourUsd.p25.toFixed(2)}–${row.vast.pricePerGpuHourUsd.p75.toFixed(2)} · {row.vast.gpuCount} 卡</small></> : "—"}
                  </td>
                  <td>{row.runpodCommunity ? <><b>${row.runpodCommunity.pricePerGpuHourUsd.toFixed(2)}</b>{stockLabel(row.runpodCommunity)}</> : stockLabel(null)}</td>
                  <td>{row.runpodSecure ? <><b>${row.runpodSecure.pricePerGpuHourUsd.toFixed(2)}</b>{stockLabel(row.runpodSecure)}</> : stockLabel(null)}</td>
                  <td className={change == null ? "" : change <= 0 ? "is-cheaper" : "is-pricier"}>{change == null ? "积累中" : signed(change, "%")}</td>
                  <td>{row.vast?.medianEffectiveVcpuPerGpu == null ? "—" : row.vast.medianEffectiveVcpuPerGpu.toFixed(0)}</td>
                  <td>{row.vast?.medianCpuRamGbPerGpu == null ? "—" : `${row.vast.medianCpuRamGbPerGpu.toFixed(0)}GB`}</td>
                  <td>{row.vast?.medianDlperfPerDollar == null ? "—" : row.vast.medianDlperfPerDollar.toFixed(0)}</td>
                </tr>;
              })}</tbody>
            </table>
            </div>
          </details>
          {selected ? <aside className="monitor-rental__detail">
            <header>
              <div><b>{selected.label}</b><small>{selected.vramGb}GB · 选中行历史</small></div>
              <strong>{spread == null ? "不可比" : `${signed(spread, "%")} vs RunPod`}</strong>
            </header>
            <RentalHistoryChart panel={panel} sku={selected.sku} label={selected.label} />
            <dl>
              <div><dt>Vast 可租供给</dt><dd>{selected.vast ? `${selected.vast.gpuCount} 卡 / ${selected.vast.offerCount} 报价` : "—"}</dd></div>
              <div><dt>Bundle $/有效 vCPU时</dt><dd>{selected.vast?.medianBundleUsdPerEffectiveVcpuHour == null ? "—" : `$${selected.vast.medianBundleUsdPerEffectiveVcpuHour.toFixed(3)}`}</dd></div>
              <div><dt>中位可靠性</dt><dd>{selected.vast ? `${selected.vast.medianReliabilityPct.toFixed(2)}%` : "—"}</dd></div>
            </dl>
            <p>这里的 CPU 是 GPU 整机附带资源；Bundle 价格包含 GPU，不能当作纯 CPU 租价。</p>
          </aside> : null}
        </div> : <div className="monitor-rental__body monitor-rental__body--cpu">
          <details className="monitor-rental__table-fold">
            <summary>展开 {visibleCpu.length} 款 CPU 云主机报价明细 · 点选一行切换下方走势图</summary>
            <div className="monitor-rental__table-wrap">
            <table className="monitor-rental__cpu-table">
              <thead><tr>
                <th>Azure CPU / RAM</th>
                <th>$/虚机时</th>
                <th>$/vCPU时</th>
                <th>$/GB RAM时</th>
                <th>7D</th>
                <th>价格生效</th>
              </tr></thead>
              <tbody>{visibleCpu.map((row) => {
                const change = cpuSevenDayChange(panel, row.sku);
                return <tr key={row.sku} className={selectedCpu?.sku === row.sku ? "is-selected" : ""}>
                  <td><button type="button" onClick={() => setSelectedCpuSku(row.sku)}><b>{row.label}</b><small>{row.vcpus} vCPU · {row.memoryGb}GB</small></button></td>
                  <td><b>${row.pricePerHourUsd.toFixed(3)}</b><small>≈ ${(row.pricePerHourUsd * 730).toFixed(0)} / 月</small></td>
                  <td>${row.pricePerVcpuHourUsd.toFixed(4)}</td>
                  <td>${row.pricePerMemoryGbHourUsd.toFixed(5)}</td>
                  <td className={change == null ? "" : change <= 0 ? "is-cheaper" : "is-pricier"}>{change == null ? "积累中" : signed(change, "%")}</td>
                  <td>{row.effectiveStartDate.slice(0, 10)}</td>
                </tr>;
              })}</tbody>
            </table>
            </div>
          </details>
          {selectedCpu ? <aside className="monitor-rental__detail">
            <header>
              <div><b>{selectedCpu.label}</b><small>Azure East US · Linux · Pay-as-you-go</small></div>
              <strong>{cpuPremium == null ? `${selectedCpu.vcpus} vCPU` : `${signed(cpuPremium, "%")} / vCPU vs D`}</strong>
            </header>
            <CpuRentalHistoryChart panel={panel} sku={selectedCpu.sku} label={selectedCpu.label} />
            <dl>
              <div><dt>独立 CPU / RAM</dt><dd>{selectedCpu.vcpus} vCPU / {selectedCpu.memoryGb}GB</dd></div>
              <div><dt>730 小时月成本</dt><dd>${(selectedCpu.pricePerHourUsd * 730).toFixed(2)}</dd></div>
              <div><dt>单位 vCPU 时</dt><dd>${selectedCpu.pricePerVcpuHourUsd.toFixed(4)}</dd></div>
            </dl>
            <p>这是独立 CPU 云主机公开价；已排除 Spot、Low Priority、Windows 与 Cloud Services 计价。</p>
          </aside> : <aside className="monitor-rental__detail"><p className="monitor-rental__empty">线上数据仍是 schema v1；CPU 日度序列将在下一次 Workflow 刷新后开始。</p></aside>}
        </div>}
      <footer>Source: Vast Search Offers API + RunPod GraphQL gpuTypes + Azure Retail Prices API；历史层：getflops.ai dated collector snapshots。全部归一到 USD 按需价，本站 Workflow 继续按日写入 R2。</footer>
    </section>
  );
}

interface DemandMetric {
  key: string;
  tabLabel: string;
  tabValue: string;
  title: string;
  subtitle: string;
  value: string;
  /** undefined = 该指标没有 MoM 徽章；null = 有徽章但暂无同比期。 */
  change?: number | null;
  chart: {
    points: Array<{ period: string; value: number }>;
    label: string;
    unit: string;
    format: (value: number) => string;
    seriesLabel?: string;
    overlays?: TrendOverlay[];
  } | null;
  pending?: string;
  takeaway: string | null;
  explain: string;
  stats: Array<{ label: string; value: string }>;
}

interface DemandAside {
  arr: Array<{
    label: string;
    reportedArrB: number;
    reportedAt: string;
    latestCompleteMonthNowcastArrB: number;
    latestCompleteMonth: string;
    statisticalCurrentDateNowcastArrB: number;
    currentDateNowcastArrB: number;
    currentDate: string;
    forecast12mArrB: number;
    forecastEndMonth: string;
    currentCalibration?: { valueB: number; asOf: string };
    anchorAgeDays: number;
    oosObservations: number;
    oosMape: number;
    meanErrorB: number;
    externalBenchmark?: { valueB: number; asOf: string; lowerBound: boolean };
  }>;
  combinedCurrentArrB: number;
  combinedStatisticalCurrentArrB: number;
  calibrationApplied: boolean;
  combinedArrBenchmark?: { valueB: number; asOf: string };
  taskMix: Array<{ key: string; label: string; tokenIntensity: number }>;
  coding: { codex: number; claude: number; codexMoM: number | null; claudeMoM: number | null };
}

function FeaturedDemandPanel({ metrics, aside, source }: { metrics: DemandMetric[]; aside: DemandAside; source: string }) {
  const [activeKey, setActiveKey] = useState(metrics[0]?.key);
  const [autoPlay, setAutoPlay] = useState(true);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = Math.max(metrics.findIndex((metric) => metric.key === activeKey), 0);
  const nextAutoKey = metrics.length ? metrics[(activeIndex + 1) % metrics.length].key : undefined;

  const selectIndex = (index: number, focusTab = false) => {
    const normalizedIndex = (index + metrics.length) % metrics.length;
    setActiveKey(metrics[normalizedIndex].key);
    if (focusTab) tabRefs.current[normalizedIndex]?.focus();
  };

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(media.matches);
    if (media.matches) setAutoPlay(false);
  }, []);

  useEffect(() => {
    if (!autoPlay || interactionPaused || metrics.length < 2 || !nextAutoKey) return;
    const timer = window.setTimeout(() => setActiveKey(nextAutoKey), 9000);
    return () => window.clearTimeout(timer);
  }, [autoPlay, interactionPaused, metrics.length, nextAutoKey]);

  useEffect(() => {
    const tabs = tabsRef.current;
    const activeTab = tabRefs.current[activeIndex];
    if (!tabs || !activeTab || tabs.scrollWidth <= tabs.clientWidth) return;
    const nextLeft = activeTab.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2;
    tabs.scrollTo({
      left: Math.max(0, Math.min(nextLeft, tabs.scrollWidth - tabs.clientWidth)),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }, [activeIndex, prefersReducedMotion]);

  const pauseForFocus = () => setInteractionPaused(true);
  const resumeAfterFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setInteractionPaused(false);
  };

  if (!metrics.length) return null;

  return (
    <section className="demand-panel" aria-label="需求侧信号：默认平台 Token 用量，可切换其他指标">
      <div className="demand-panel__switcher" aria-label="首页指标轮播控制">
        <button
          type="button"
          className="demand-panel__step"
          aria-label={`上一项：${metrics[(activeIndex - 1 + metrics.length) % metrics.length].tabLabel}`}
          onClick={() => selectIndex(activeIndex - 1)}
        >
          <CaretLeft size={18} weight="bold" aria-hidden="true" />
        </button>

        <div
          ref={tabsRef}
          className="demand-panel__tabs"
          role="tablist"
          aria-label="选择要看的需求指标"
          onFocusCapture={pauseForFocus}
          onBlurCapture={resumeAfterFocus}
        >
          {metrics.map((metric, index) => (
            <button
              key={metric.key}
              ref={(node) => { tabRefs.current[index] = node; }}
              id={`demand-tab-${metric.key}`}
              type="button"
              role="tab"
              tabIndex={index === activeIndex ? 0 : -1}
              aria-selected={index === activeIndex}
              aria-controls={`demand-tabpanel-${metric.key}`}
              className={index === activeIndex ? "is-active" : ""}
              onClick={() => selectIndex(index)}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  selectIndex(index - 1, true);
                } else if (event.key === "ArrowRight") {
                  event.preventDefault();
                  selectIndex(index + 1, true);
                } else if (event.key === "Home") {
                  event.preventDefault();
                  selectIndex(0, true);
                } else if (event.key === "End") {
                  event.preventDefault();
                  selectIndex(metrics.length - 1, true);
                }
              }}
            >
              <span>{metric.tabLabel}</span>
              <b>{metric.tabValue}</b>
            </button>
          ))}
        </div>

        <div className="demand-panel__carousel-status">
          <output aria-live="polite" aria-atomic="true">
            <b>{String(activeIndex + 1).padStart(2, "0")}</b>
            <span>/ {String(metrics.length).padStart(2, "0")}</span>
          </output>
          <button
            type="button"
            aria-label={autoPlay ? "暂停首页指标自动轮播" : "开始首页指标自动轮播"}
            aria-pressed={autoPlay}
            title={autoPlay ? "自动轮播中；悬停或聚焦时暂停" : "开始自动轮播"}
            onClick={() => setAutoPlay((current) => !current)}
          >
            {autoPlay ? <Pause size={14} weight="fill" aria-hidden="true" /> : <Play size={14} weight="fill" aria-hidden="true" />}
          </button>
        </div>

        <button
          type="button"
          className="demand-panel__step"
          aria-label={`下一项：${metrics[(activeIndex + 1) % metrics.length].tabLabel}`}
          onClick={() => selectIndex(activeIndex + 1)}
        >
          <CaretRight size={18} weight="bold" aria-hidden="true" />
        </button>
      </div>

      <div
        className="demand-panel__body"
        onMouseEnter={() => setInteractionPaused(true)}
        onMouseLeave={() => setInteractionPaused(false)}
        onFocusCapture={pauseForFocus}
        onBlurCapture={resumeAfterFocus}
      >
        <div className="demand-panel__mains">
          {metrics.map((metric, index) => (
            <div
              key={metric.key}
              id={`demand-tabpanel-${metric.key}`}
              role="tabpanel"
              aria-labelledby={`demand-tab-${metric.key}`}
              className="demand-panel__main"
              hidden={index !== activeIndex}
            >
              <header className="demand-panel__head">
                <div>
                  <h2>{metric.title}</h2>
                  <small>{metric.subtitle}</small>
                </div>
                <div className="demand-panel__now">
                  <strong>{metric.value}</strong>
                  {metric.change === undefined ? null : <Change value={metric.change} />}
                </div>
              </header>

              {metric.chart ? (
                <div className="demand-panel__chart-well">
                  <MonitorTrendChart
                    points={metric.chart.points}
                    label={metric.chart.label}
                    unit={metric.chart.unit}
                    format={metric.chart.format}
                    seriesLabel={metric.chart.seriesLabel}
                    overlays={metric.chart.overlays}
                  />
                </div>
              ) : (
                <p className="demand-panel__pending">{metric.pending ?? "该指标的历史仍在积累。"}</p>
              )}

              {metric.takeaway ? (
                <p className="demand-panel__takeaway"><b>最新完整月读数</b>{metric.takeaway}</p>
              ) : null}

              {metric.stats.length ? (
                <dl className="demand-panel__stats">
                  {metric.stats.map((stat) => (
                    <div key={stat.label}>
                      <dt>{stat.label}</dt>
                      <dd>{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}

              <div className="demand-panel__read">
                <h3>怎么读这张图</h3>
                <p>{metric.explain}</p>
              </div>

              <p className="demand-panel__source">{source}</p>
            </div>
          ))}
        </div>

        <aside className="demand-panel__aside">
          <div className="demand-panel__card" id="lab-arr-live-card">
            <header><span>AI Labs 当前 ARR</span><small>当前收入速度 × 12 · 非 GAAP</small></header>
            <dl className="demand-panel__arr">
              {aside.arr.map((row) => {
                const bias = Math.abs(row.meanErrorB) < 0.05
                  ? "历史无明显偏差"
                  : row.meanErrorB < 0
                    ? `历史平均低估 $${Math.abs(row.meanErrorB).toFixed(1)}B`
                    : `历史平均高估 $${row.meanErrorB.toFixed(1)}B`;
                return (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>${row.currentDateNowcastArrB.toFixed(1)}B <small>当前 ARR · {row.currentDate}</small></dd>
                    {row.currentCalibration ? <span className="demand-panel__arr-benchmark">外部研究校准 ${row.currentCalibration.valueB.toFixed(0)}B · 非公开披露</span> : null}
                    <span>纯模型 nowcast ${row.statisticalCurrentDateNowcastArrB.toFixed(1)}B</span>
                    <span>公开锚点 ${row.reportedArrB.toFixed(1)}B · {row.reportedAt.slice(0, 7)}</span>
                    <span>完整月纯模型 ARR ${row.latestCompleteMonthNowcastArrB.toFixed(1)}B · {row.latestCompleteMonth}</span>
                    {row.externalBenchmark ? <span>公开外参 {row.externalBenchmark.lowerBound ? ">" : "≈"}${row.externalBenchmark.valueB.toFixed(0)}B · {row.externalBenchmark.asOf}</span> : null}
                    <span>{row.forecastEndMonth} 月末预测 ARR ${row.forecast12mArrB.toFixed(0)}B · 不是累计收入</span>
                    <span className="demand-panel__arr-bias">OOS {row.oosObservations} · MAPE {row.oosMape.toFixed(1)}% · {bias}</span>
                    <span>披露锚点已 {row.anchorAgeDays} 天未更新</span>
                  </div>
                );
              })}
            </dl>
            {aside.combinedArrBenchmark ? (
              <p className="demand-panel__arr-reference">
                {aside.calibrationApplied ? "当前外部校准" : "当前纯模型"} ARR 合计 ${aside.combinedCurrentArrB.toFixed(0)}B；纯模型 ${aside.combinedStatisticalCurrentArrB.toFixed(1)}B。SemiAnalysis {aside.combinedArrBenchmark.asOf} 公开摘要仅披露两家约 ${aside.combinedArrBenchmark.valueB.toFixed(0)}B，精确拆分未公开且 gross / net 口径可能不同。
              </p>
            ) : null}
          </div>

          <div className="demand-panel__card">
            <header><span>7 日任务 Token 强度</span><small>Token 份额 ÷ 请求份额</small></header>
            <div className="demand-panel__tasks">
              {aside.taskMix.map((row) => (
                <p key={row.key}><b>{row.label}</b><em>{row.tokenIntensity.toFixed(2)}×</em></p>
              ))}
            </div>
          </div>

          <div className="demand-panel__card demand-panel__card--muted">
            <header><span>Agent Coding 采用</span><small>次要 · npm 下载，非用量</small></header>
            <div className="demand-panel__tasks">
              <p><b>Codex {compact(aside.coding.codex)}</b><em>{aside.coding.codexMoM == null ? "—" : signed(aside.coding.codexMoM, "% MoM")}</em></p>
              <p><b>Claude Code {compact(aside.coding.claude)}</b><em>{aside.coding.claudeMoM == null ? "—" : signed(aside.coding.claudeMoM, "% MoM")}</em></p>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

export function RealtimeMonitorBoard({
  result,
}: {
  result: ResearchModelBundle;
}) {
  const token = result.tokenVolume.monitor;
  const labs = result.labArr.monitor.companies;
  const openai = labs.find((company) => company.company === "openai")!;
  const anthropic = labs.find((company) => company.company === "anthropic")!;
  const tokenHistory = token.openRouter.history.slice(-12);
  const market = token.openRouter.market;
  const marketHistory = market?.history.slice(-12) ?? [];
  const latestMarket = marketHistory.at(-1) ?? null;
  const latestPlatform = tokenHistory.at(-1)!;
  const priorPlatform = tokenHistory.at(-2)!;
  const openAiSharePct = latestPlatform.openAiTokensT / latestPlatform.tokensT * 100;
  const anthropicSharePct = latestPlatform.anthropicTokensT / latestPlatform.tokensT * 100;
  const priorOpenAiSharePct = priorPlatform.openAiTokensT / priorPlatform.tokensT * 100;
  const priorAnthropicSharePct = priorPlatform.anthropicTokensT / priorPlatform.tokensT * 100;
  const capex = result.companyCapex.monitor;
  const priorCapex = [...result.companyCapex.backtest.total.points]
    .reverse()
    .find((point) => point.period < capex.commonPeriod)?.actual ?? null;
  const capexChangePct = priorCapex && priorCapex > 0 ? (capex.totalCapex / priorCapex - 1) * 100 : null;
  const deliveredCompute = result.computeDeployment?.monitor.metrics.find((metric) => metric.id === "delivered_compute_index") ?? null;
  const power = result.powerDemand?.monitor ?? null;
  const modelTape = market?.modelTape?.rows.slice(0, 5) ?? [];
  const parameterDisclosureCount = modelTape.filter((row) => row.publicParametersB != null).length;

  const mom = token.openRouter.monthOverMonthPct;
  const yoy = token.openRouter.yearOverYearPct;
  const firstPlatform = tokenHistory[0];
  const tokenMultiple = firstPlatform.tokensT > 0 ? latestPlatform.tokensT / firstPlatform.tokensT : null;
  const threeMonthBase = tokenHistory.at(-4) ?? null;
  const threeMonthGrowthPct = threeMonthBase && threeMonthBase.tokensT > 0
    ? (Math.pow(latestPlatform.tokensT / threeMonthBase.tokensT, 1 / 3) - 1) * 100
    : null;
  const doublingMonths = threeMonthGrowthPct != null && threeMonthGrowthPct > 0
    ? Math.log(2) / Math.log(1 + threeMonthGrowthPct / 100)
    : null;
  const demandMetrics: DemandMetric[] = [
    {
      key: "token",
      tabLabel: "平台 Token 用量",
      tabValue: `${token.openRouter.latestTokensT.toFixed(1)}T`,
      title: "OpenRouter 平台 Token 用量",
      subtitle: "月度总量，双线拆出 OpenAI / Anthropic 贡献 · 单一平台窗口",
      value: `${token.openRouter.latestTokensT.toFixed(1)}T`,
      change: mom,
      chart: {
        points: tokenHistory.map((row) => ({ period: row.month, value: row.tokensT })),
        label: "OpenRouter 最近十二个完整月 Token 趋势，含 OpenAI 与 Anthropic 分线",
        unit: "Token（万亿）",
        format: (value) => `${value.toLocaleString("zh-CN", { maximumFractionDigits: value >= 100 ? 0 : 1 })}T`,
        seriesLabel: "平台总量",
        overlays: [
          { label: "OpenAI 系", className: "monitor-overlay--openai", values: tokenHistory.map((row) => row.openAiTokensT) },
          { label: "Anthropic 系", className: "monitor-overlay--anthropic", values: tokenHistory.map((row) => row.anthropicTokensT) },
        ],
      },
      takeaway: tokenMultiple == null ? null
        : `${firstPlatform.month} 以来累计 ×${tokenMultiple.toFixed(1)}；近 3 月复合 ${threeMonthGrowthPct == null ? "—" : signed(threeMonthGrowthPct, "%")}/月${doublingMonths == null ? "，增长暂停" : `，按此速度约 ${Math.round(doublingMonths)} 个月翻倍`}${yoy == null ? "" : `；同比 ${signed(yoy, "%")}`}。`,
      explain:
        "OpenRouter 平台上真实跑过的 Token 总量，是「需求」链最直接的信号。实线是平台总量，两条虚线拆出 OpenAI 系与 Anthropic 系模型的贡献——先看总量还在不在加速，再看增长由谁驱动。这是单一平台窗口，不代表全球总量。",
      stats: [
        { label: "环比", value: mom == null ? "暂无同比" : `${signed(mom, "%")} MoM` },
        { label: "同比", value: yoy == null ? "不足 12 个月" : `${signed(yoy, "%")} YoY` },
        { label: "连续完整月", value: `${tokenHistory.length} 个` },
        { label: "OpenAI Token 份额", value: `${openAiSharePct.toFixed(1)}%（${signed(openAiSharePct - priorOpenAiSharePct, "pp")}）` },
        { label: "Anthropic Token 份额", value: `${anthropicSharePct.toFixed(1)}%（${signed(anthropicSharePct - priorAnthropicSharePct, "pp")}）` },
      ],
    },
  ];

  if (market && latestMarket) {
    const intelligenceHistory = marketHistory.flatMap((row) => row.weightedIntelligenceIndex == null ? [] : [{ period: row.month, value: row.weightedIntelligenceIndex }]);
    const callSizeHistory = marketHistory.flatMap((row) => row.publicAppTokensPerRequest == null ? [] : [{ period: row.month, value: row.publicAppTokensPerRequest }]);
    const callCostHistory = marketHistory.flatMap((row) => row.standardCallCostUsd == null ? [] : [{ period: row.month, value: row.standardCallCostUsd }]);
    const intelSpan = intelligenceHistory.length >= 2 ? { from: intelligenceHistory[0], to: intelligenceHistory.at(-1)! } : null;
    const costSpan = callCostHistory.length >= 2 ? { from: callCostHistory[0], to: callCostHistory.at(-1)! } : null;
    const intelPerDollarPct = intelSpan && costSpan && intelSpan.from.value > 0 && costSpan.from.value > 0 && costSpan.to.value > 0
      ? ((intelSpan.to.value / costSpan.to.value) / (intelSpan.from.value / costSpan.from.value) - 1) * 100
      : null;
    const firstMarket = marketHistory[0];
    const leaderChanges = marketHistory.filter((row, index) => index > 0 && row.leaderModelLabel !== marketHistory[index - 1].leaderModelLabel).length;
    const callSizeSpan = callSizeHistory.length >= 2 ? { from: callSizeHistory[0], to: callSizeHistory.at(-1)! } : null;
    demandMetrics.push(
      {
        key: "intelligence",
        tabLabel: "智力 × 价格",
        tabValue: latestMarket.weightedIntelligenceIndex?.toFixed(1) ?? "—",
        title: "智力 × 价格：效率前沿",
        subtitle: "Artificial Analysis 智力指数，经 OpenRouter 模型 mix 加权",
        value: latestMarket.weightedIntelligenceIndex?.toFixed(1) ?? "—",
        chart: intelligenceHistory.length >= 2 ? {
          points: intelligenceHistory,
          label: "OpenRouter Token 加权 Artificial Analysis 智力指数",
          unit: "AA Intelligence Index",
          format: (value) => value.toFixed(1),
        } : null,
        pending: "等待可用的 AA 基准覆盖。",
        takeaway: intelSpan == null ? null
          : `${intelSpan.from.period} 以来加权智力 ${intelSpan.from.value.toFixed(1)} → ${intelSpan.to.value.toFixed(1)}${costSpan ? `，标准调用成本 $${costSpan.from.value.toFixed(3)} → $${costSpan.to.value.toFixed(3)}` : ""}${intelPerDollarPct == null ? "" : `；每美元买到的智力 ${signed(intelPerDollarPct, "%")}${intelPerDollarPct >= 0 ? "——需求扩张背后的效率引擎" : "——效率红利收窄，值得警惕"}`}。`,
        explain:
          "把市场当月真实用的模型按 Token 份额加权，得到「平台平均智力」。指数上行 = 用户在用更聪明的模型；配合下方的标准调用成本（固定 10k 输入 / 1.5k 输出），就能回答「每美元买到多少智力」。速度/加速度是指数的一阶与二阶变化。",
        stats: [
          { label: "速度", value: market.signals.intelligenceVelocityPerMonth == null ? "—" : signed(market.signals.intelligenceVelocityPerMonth, " 分/月") },
          { label: "加速度", value: market.signals.intelligenceAcceleration == null ? "—" : signed(market.signals.intelligenceAcceleration, " 分/月²") },
          { label: "标准调用成本", value: `$${latestMarket.standardCallCostUsd?.toFixed(3) ?? "—"}${market.signals.standardCallCostMonthOverMonthPct == null ? "" : `（${signed(market.signals.standardCallCostMonthOverMonthPct, "%")} MoM）`}` },
          { label: "输入牌价", value: `$${latestMarket.weightedInputPricePerMillionUsd?.toFixed(2) ?? "—"}/M` },
          { label: "输出牌价", value: `$${latestMarket.weightedOutputPricePerMillionUsd?.toFixed(2) ?? "—"}/M` },
          { label: "基准覆盖", value: `${latestMarket.intelligenceCoveragePct?.toFixed(0) ?? "—"}%` },
        ],
      },
      {
        key: "concentration",
        tabLabel: "模型竞争",
        tabValue: `Top5 ${latestMarket.top5SharePct.toFixed(1)}%`,
        title: "模型竞争格局",
        subtitle: "OpenRouter 月度模型榜 · Top 5 Token 集中度",
        value: `Top 5 ${latestMarket.top5SharePct.toFixed(1)}%`,
        chart: {
          points: marketHistory.map((row) => ({ period: row.month, value: row.top5SharePct })),
          label: "OpenRouter 模型 Top 5 Token 集中度",
          unit: "平台 Token 份额",
          format: (value) => `${value.toFixed(1)}%`,
        },
        takeaway: `${firstMarket.month} 以来 Top 5 份额 ${firstMarket.top5SharePct.toFixed(1)}% → ${latestMarket.top5SharePct.toFixed(1)}%（${signed(latestMarket.top5SharePct - firstMarket.top5SharePct, "pp")}，${latestMarket.top5SharePct >= firstMarket.top5SharePct ? "流量向头部集中" : "流量向头部之外扩散"}）；${marketHistory.length} 个月里月冠军更替 ${leaderChanges} 次。`,
        explain:
          "曲线是 Top 5 模型合计占平台 Token 的比例：走高 = 少数模型主导，走低 = 竞争分散。月冠军是当月用量第一的模型；日冠军数和新晋模型份额衡量格局翻动的速度——冠军换得越勤，护城河越浅。",
        stats: [
          { label: "月冠军", value: `${latestMarket.leaderModelLabel}（${latestMarket.leaderSharePct.toFixed(1)}%）` },
          { label: "日冠军", value: `${latestMarket.dailyLeaderCount} 个` },
          { label: "新晋份额", value: `${latestMarket.firstEntryTokenSharePct?.toFixed(1) ?? "—"}%` },
          { label: "上榜模型", value: `${latestMarket.rankedModelCount} 个` },
          { label: "长尾份额", value: `${latestMarket.tailSharePct.toFixed(1)}%` },
        ],
      },
      {
        key: "callsize",
        tabLabel: "平均调用尺寸",
        tabValue: latestMarket.publicAppTokensPerRequest == null ? "—" : compact(latestMarket.publicAppTokensPerRequest),
        title: "平均调用尺寸",
        subtitle: `Top ${latestMarket.publicAppCount ?? "—"} 公开应用 · 加权平均 Token / 请求`,
        value: latestMarket.publicAppTokensPerRequest == null ? "—" : `${compact(latestMarket.publicAppTokensPerRequest)} Token`,
        change: market.signals.tokensPerRequestMonthOverMonthPct,
        chart: callSizeHistory.length >= 2 ? {
          points: callSizeHistory,
          label: "OpenRouter Top 100 公开应用加权平均 Token 每请求",
          unit: "Token / 请求",
          format: compact,
        } : null,
        pending: "Workflow 正在建立 12 个月调用尺寸历史。",
        takeaway: callSizeSpan && callSizeSpan.from.value > 0
          ? `${callSizeSpan.from.period} 以来单次调用 ${compact(callSizeSpan.from.value)} → ${compact(callSizeSpan.to.value)} Token（×${(callSizeSpan.to.value / callSizeSpan.from.value).toFixed(1)}）——调用越重，同样的请求数吃掉的算力越多。`
          : null,
        explain:
          "Top 100 公开应用每次请求的加权平均 Token。上升 = 单次调用更「重」：更长上下文、更多工具轮次、Agent 化。这是需求质变的信号——即使请求数不变，Token 与算力消耗也在涨。真实 requests × Token，不含隐藏/私有应用。",
        stats: [
          { label: "环比", value: market.signals.tokensPerRequestMonthOverMonthPct == null ? "等待第二个可比月" : `${signed(market.signals.tokensPerRequestMonthOverMonthPct, "%")} MoM` },
          { label: "覆盖请求量", value: latestMarket.publicAppRequests == null ? "—" : `${compact(latestMarket.publicAppRequests)} 次/月` },
          { label: "覆盖应用", value: `${latestMarket.publicAppCount ?? "—"} 个` },
        ],
      },
    );
  }

  const demandAside: DemandAside = {
    arr: [
      {
        label: "OpenAI",
        reportedArrB: openai.latestReportedArrB,
        reportedAt: openai.reportedAt,
        latestCompleteMonthNowcastArrB: openai.latestCompleteMonthNowcastArrB,
        latestCompleteMonth: result.labArr.monitor.latestCompleteMonth,
        statisticalCurrentDateNowcastArrB: openai.statisticalCurrentDateNowcastArrB,
        currentDateNowcastArrB: openai.currentDateNowcastArrB,
        currentDate: result.labArr.monitor.asOf,
        forecast12mArrB: result.labArr.companies.openai.forecast.points.at(-1)!.totalArrB,
        forecastEndMonth: result.labArr.companies.openai.forecast.points.at(-1)!.month,
        currentCalibration: openai.currentCalibration ? { valueB: openai.currentCalibration.valueB, asOf: openai.currentCalibration.asOf } : undefined,
        anchorAgeDays: openai.anchorAgeDays,
        oosObservations: result.labArr.companies.openai.backtest.primary.metrics.full.observations,
        oosMape: result.labArr.companies.openai.backtest.primary.metrics.full.mape,
        meanErrorB: result.labArr.companies.openai.backtest.biasDiagnostics.fullWindow.meanErrorB,
      },
      {
        label: "Anthropic",
        reportedArrB: anthropic.latestReportedArrB,
        reportedAt: anthropic.reportedAt,
        latestCompleteMonthNowcastArrB: anthropic.latestCompleteMonthNowcastArrB,
        latestCompleteMonth: result.labArr.monitor.latestCompleteMonth,
        statisticalCurrentDateNowcastArrB: anthropic.statisticalCurrentDateNowcastArrB,
        currentDateNowcastArrB: anthropic.currentDateNowcastArrB,
        currentDate: result.labArr.monitor.asOf,
        forecast12mArrB: result.labArr.companies.anthropic.forecast.points.at(-1)!.totalArrB,
        forecastEndMonth: result.labArr.companies.anthropic.forecast.points.at(-1)!.month,
        currentCalibration: anthropic.currentCalibration ? { valueB: anthropic.currentCalibration.valueB, asOf: anthropic.currentCalibration.asOf } : undefined,
        anchorAgeDays: anthropic.anchorAgeDays,
        oosObservations: result.labArr.companies.anthropic.backtest.primary.metrics.full.observations,
        oosMape: result.labArr.companies.anthropic.backtest.primary.metrics.full.mape,
        meanErrorB: result.labArr.companies.anthropic.backtest.biasDiagnostics.fullWindow.meanErrorB,
        externalBenchmark: result.labArr.monitor.externalBenchmarks
          .filter((benchmark) => benchmark.scope === "anthropic" && !benchmark.calibrationEligible)
          .map((benchmark) => ({ valueB: benchmark.valueB, asOf: benchmark.asOf, lowerBound: benchmark.exactness === "lower-bound" }))
          .at(-1),
      },
    ],
    combinedCurrentArrB: result.labArr.monitor.combinedCurrentDateNowcastArrB,
    combinedStatisticalCurrentArrB: result.labArr.monitor.combinedStatisticalCurrentDateNowcastArrB,
    calibrationApplied: Boolean(openai.currentCalibration && anthropic.currentCalibration),
    combinedArrBenchmark: result.labArr.monitor.externalBenchmarks
      .filter((benchmark) => benchmark.scope === "combined")
      .map((benchmark) => ({ valueB: benchmark.valueB, asOf: benchmark.asOf }))
      .at(-1),
    taskMix: market?.taskMix.rows.slice(0, 5).map((row) => ({ key: row.key, label: row.label, tokenIntensity: row.tokenIntensity })) ?? [],
    coding: {
      codex: token.codingAdoption.codexDownloads,
      claude: token.codingAdoption.claudeCodeDownloads,
      codexMoM: token.codingAdoption.codexMonthOverMonthPct,
      claudeMoM: token.codingAdoption.claudeMonthOverMonthPct,
    },
  };

  return (
    <section className="realtime-monitor" aria-label="实时监控：需求、变现、算力租赁、CapEx 与电力四条链">
      <FeaturedDemandPanel
        metrics={demandMetrics}
        aside={demandAside}
        source="Source: OpenRouter (openrouter.ai/rankings)；Artificial Analysis via OpenRouter；ARR 是当前收入速度的年化值，不是未来 12 个月累计收入；公开锚点、纯模型 nowcast、外部校准与未来预测严格分层。"
      />

      {modelTape.length ? <section className="monitor-model-tape" aria-label="模型用量、智力与牌价快照">
        <header>
          <div><span>模型 Tape</span><small>{market?.modelTape?.month} · OpenRouter Token 排名</small></div>
          <p>参数披露 <b>{parameterDisclosureCount}/{modelTape.length}</b>；未公开就保持空值</p>
        </header>
        {modelTape.map((row, index) => <article key={row.modelId} title={row.label}>
          <span><i>{index + 1}</i>{row.label}</span>
          <b>{row.tokenSharePct.toFixed(1)}%</b>
          <small>智力 {row.intelligenceIndex?.toFixed(1) ?? "—"}</small>
          <small>入 ${row.inputPricePerMillionUsd?.toFixed(2) ?? "—"} / 出 ${row.outputPricePerMillionUsd?.toFixed(2) ?? "—"}</small>
          <em>{row.publicParametersB == null ? "参数未披露" : `${row.publicParametersB.toFixed(0)}B 参数`}</em>
        </article>)}
      </section> : null}

      <ComputeRentalBoard panel={result.rentalMarket} />

      <div className="monitor-live-strip" aria-label="资本周期自动更新信号">
        <article>
          <header><span>平台厂商构成</span><small>{latestPlatform.month}</small></header>
          <div className="monitor-live-strip__pair">
            <p><b>OpenAI {openAiSharePct.toFixed(1)}%</b><em>{signed(openAiSharePct - priorOpenAiSharePct, "pp MoM")}</em></p>
            <p><b>Anthropic {anthropicSharePct.toFixed(1)}%</b><em>{signed(anthropicSharePct - priorAnthropicSharePct, "pp MoM")}</em></p>
          </div>
          <footer>OpenRouter 内可识别模型 Token 份额</footer>
        </article>
        <article>
          <header><span>五家公司现金 CapEx</span><small>{capex.commonPeriod}</small></header>
          <strong>${capex.totalCapex.toFixed(1)}B</strong>
          <em>{capexChangePct == null ? "缺少可比季度" : signed(capexChangePct, "% QoQ")}</em>
          <footer>SEC first-reported 历史实际 · {capex.companies.length} 家</footer>
        </article>
        <article>
          <header><span>交付算力指数</span><small>{result.computeDeployment?.monitor.period ?? "—"}</small></header>
          <strong>{deliveredCompute ? deliveredCompute.value.toFixed(1) : "—"}</strong>
          <em>{deliveredCompute ? `${deliveredCompute.coveragePct.toFixed(0)}% 可测覆盖` : "输入尚未发布"}</em>
          <footer>芯片交付研究指数 · 不是 GPU 数量</footer>
        </article>
        <article>
          <header><span>设施电力负荷</span><small>{power?.period ?? "—"}</small></header>
          <strong>{power ? `${power.facilityLoadGw.value.toFixed(1)} GW` : "—"}</strong>
          <em>{power ? `${power.facilityLoadGw.low.toFixed(1)} — ${power.facilityLoadGw.high.toFixed(1)} GW` : "输入尚未发布"}</em>
          <footer>{power ? `物理估计 · Epoch ${power.epochCoverage.coveredSites} 个站点交叉验证` : "研究基线"}</footer>
        </article>
      </div>
    </section>
  );
}
